import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import type { IconKind } from '../Icon/Icon';
import { ViewportDrawer } from '../Overlay';
import { CL_DISPOSITIONS } from '../../comm-log/types';
import type { ClAgent, ClChannel, ClRow, ClVerdict } from '../../comm-log/types';

export interface CommLogDetailDrawerProps {
  row: ClRow | null;
  onClose: () => void;
  /** Open the patient's record (when the row is matched to a chart). */
  onOpenPatient?: (row: ClRow) => void;
}

const AGENT_LABEL: Record<ClAgent, string> = {
  receptionist: 'AI Receptionist',
  sms: 'AI SMS',
  email: 'AI Email',
  reminders: 'Appointment Reminders',
  recall: 'Recall',
  'gap-filler': 'Gap Filler',
  treatment: 'Treatment Coordinator',
  invisalign: 'Invisalign',
  ar: 'Accounts receivable',
};

const CHANNEL_LABEL: Record<ClChannel, string> = { text: 'Text', email: 'Email', call: 'Call' };
const CHANNEL_ICON: Record<ClChannel, IconKind> = { text: 'chat', email: 'mail', call: 'phone' };

function verdictPill(v: ClVerdict): { label: string; fg: string; bg: string } {
  switch (v) {
    case 'sent':
      return { label: 'Sent', fg: 'var(--text-secondary)', bg: 'var(--chip-fill)' };
    case 'held':
      return { label: 'Held', fg: 'var(--warn-amber)', bg: 'var(--warn-amber-fill)' };
    case 'suppressed':
      return { label: 'Suppressed', fg: 'var(--warn-amber)', bg: 'var(--warn-amber-fill)' };
    case 'skipped':
      return { label: 'Skipped', fg: 'var(--text-muted)', bg: 'var(--chip-fill)' };
    case 'blocked':
      return { label: 'Blocked', fg: 'var(--alert-red)', bg: 'var(--alert-red-fill)' };
    default:
      return { label: 'Failed', fg: 'var(--alert-red)', bg: 'var(--alert-red-fill)' };
  }
}

function dispositionPill(d: string): { label: string; fg: string; bg: string } {
  const entry = CL_DISPOSITIONS.find((x) => x.id === d);
  const colour = entry?.colour ?? 'var(--text-muted)';
  return { label: entry?.label ?? d, fg: colour, bg: `color-mix(in srgb, ${colour} 12%, transparent)` };
}

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m)}:${s.toString().padStart(2, '0')}`;
}

function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return `${parts[0]?.[0] ?? ''}${parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : ''}`.toUpperCase();
}

const outcomeFg = (t: ClRow['outcomeTone']) => (t === 'good' ? 'var(--money-green)' : t === 'alert' ? 'var(--alert-red)' : 'var(--text-secondary)');

/** An uppercase section label, matching the AI Receptionist call-logs drawer. */
function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div className="text-[10.5px] font-bold uppercase tracking-[0.1em] text-[var(--text-muted)]">{children}</div>;
}

/** A label / value row in the captured section. */
function Captured({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-baseline gap-3 border-t border-[var(--border-default)] px-1 py-2.5 text-[12.5px] first:border-t-0">
      <span className="text-[11px] font-semibold uppercase tracking-[0.04em] text-[var(--text-muted)]">{label}</span>
      <span className="text-[var(--text-secondary)]">{children}</span>
    </div>
  );
}

/**
 * The comm-log detail drawer, restyled to the AI Receptionist call-logs shape
 * (2 Oct): a roomy avatar header with status pills, an actions row, the audio
 * player, a "What ELVA captured" label/value block, and the conversation as
 * chat bubbles (call transcript) or the message itself (text / email). Same data,
 * clearer shape.
 */
export function CommLogDetailDrawer({ row, onClose, onOpenPatient }: CommLogDetailDrawerProps) {
  const open = row !== null;
  return (
    <ViewportDrawer open={open} onClose={onClose} width="min(540px, 96vw)">
      {row ? (
        (() => {
          const matched = row.patient !== '';
          const title = matched ? row.patient : (row.callerNumber ?? 'Unknown caller');
          const pill = row.disposition !== undefined ? dispositionPill(row.disposition) : verdictPill(row.verdict);
          const isCall = row.channel === 'call';
          const hasAudio = isCall && (row.hasRecording === true || (row.durationSec ?? 0) > 0);
          return (
            <div className="flex h-full flex-col bg-[var(--surface-card)]">
              {/* header */}
              <header className="border-b border-[var(--border-default)] px-6 pb-[18px] pt-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--brand-indigo-tint)] text-[14px] font-bold text-[var(--brand-indigo)]">
                    {matched ? initials(title) : <Icon kind={CHANNEL_ICON[row.channel]} size={18} stroke={2} />}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h2 className="m-0 text-[20px] font-semibold leading-[1.15] tracking-[-0.02em] text-[var(--text-primary)]">{title}</h2>
                    <div className="mt-0.5 font-[var(--font-mono)] text-[11.5px] font-medium text-[var(--text-secondary)]">
                      {matched && row.callerNumber ? `${row.callerNumber} · ` : ''}{CHANNEL_LABEL[row.channel]} · {AGENT_LABEL[row.agent]}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold" style={{ color: pill.fg, background: pill.bg }}>{pill.label}</span>
                      <span className="inline-flex items-center gap-1.5 text-[11.5px] font-medium text-[var(--text-secondary)]">
                        <span className="h-2 w-2 rounded-full" style={{ background: outcomeFg(row.outcomeTone) }} />
                        {row.outcome}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 text-[11.5px] text-[var(--text-muted)]">
                      <Icon kind="clock" size={12} stroke={2} />
                      <span className="font-[var(--font-mono)]">{row.absoluteTime}</span>
                      {isCall && (row.durationSec ?? 0) > 0 ? <span>· {fmtDuration(row.durationSec ?? 0)}</span> : null}
                      {!matched ? <span>· Not in the chart</span> : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--border-default)] bg-[var(--surface-card)] text-[var(--text-secondary)] shadow-[var(--sh-1)] hover:bg-[var(--surface-sunken)]"
                  >
                    <Icon kind="x" size={15} stroke={2} />
                  </button>
                </div>

                {/* actions */}
                <div className="mt-3.5 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px] font-semibold text-[var(--text-secondary)]">
                  {hasAudio ? (
                    <button type="button" className="inline-flex items-center gap-1.5 hover:text-[var(--text-primary)]">
                      <Icon kind="download" size={13} stroke={2} /> Download audio
                    </button>
                  ) : null}
                  {matched ? (
                    <button type="button" onClick={() => onOpenPatient?.(row)} className="inline-flex items-center gap-1.5 hover:text-[var(--text-primary)]">
                      <Icon kind="team" size={13} stroke={2} /> Open patient
                    </button>
                  ) : null}
                </div>
              </header>

              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                {/* audio player (call) */}
                {hasAudio ? (
                  <div className="mb-5 flex items-center gap-3 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3 py-2.5 shadow-[var(--sh-1)]">
                    <button type="button" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--money-green)] text-white" aria-label="Play recording">
                      <Icon kind="play" size={14} stroke={2} />
                    </button>
                    <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">0:00</span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--surface-sunken)]">
                      <div className="h-full w-0 rounded-full bg-[var(--money-green)]" />
                    </div>
                    <span className="font-[var(--font-mono)] text-[11px] text-[var(--text-muted)]">{fmtDuration(row.durationSec ?? 0)}</span>
                  </div>
                ) : null}

                {/* why it did not send — a toned callout */}
                {row.verdict !== 'sent' && row.reason && row.disposition === undefined ? (
                  <div
                    className={cn(
                      'mb-5 rounded-[12px] border px-3.5 py-3 text-[12.5px] leading-[1.55] text-[var(--text-secondary)]',
                      row.verdict === 'blocked' || row.verdict === 'failed'
                        ? 'border-[var(--alert-red-line)] bg-[var(--alert-red-tint)]'
                        : 'border-[var(--warn-amber-line)] bg-[var(--warn-amber-fill)]',
                    )}
                  >
                    <div className="mb-0.5 text-[10.5px] font-bold uppercase tracking-[0.1em]" style={{ color: row.verdict === 'blocked' || row.verdict === 'failed' ? 'var(--alert-red)' : 'var(--warn-amber)' }}>
                      Why it did not send
                    </div>
                    {row.reason}
                  </div>
                ) : null}

                {/* what ELVA captured */}
                <Eyebrow>What ELVA captured</Eyebrow>
                <div className="mt-2.5 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-card)] px-3.5 py-1">
                  <Captured label="Purpose">{row.purpose}</Captured>
                  {row.appointmentReason !== undefined ? <Captured label="Reason">{row.appointmentReason}</Captured> : null}
                  <Captured label="Outcome"><span style={{ color: outcomeFg(row.outcomeTone) }} className="font-semibold">{row.outcome}</span></Captured>
                  {row.rank ? <Captured label="Rank">{row.rank}</Captured> : null}
                  {row.shared ? <Captured label="Consolidation">Shared send — counted by both campaigns</Captured> : null}
                  {row.onBehalfOf ? <Captured label="Placed by">AI Receptionist, credited to {AGENT_LABEL[row.agent]}</Captured> : null}
                </div>

                {/* batch names */}
                {row.batchNames && row.batchNames.length > 0 ? (
                  <div className="mt-5">
                    <Eyebrow>{row.batchNames.length} patients</Eyebrow>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {row.batchNames.map((n) => (
                        <span key={n} className="rounded-full bg-[var(--surface-sunken)] px-2.5 py-1 text-[11.5px] text-[var(--text-secondary)]">{n}</span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* conversation — transcript as chat bubbles, or the message */}
                {isCall && row.transcript && row.transcript.length > 0 ? (
                  <div className="mt-5">
                    <Eyebrow>Conversation</Eyebrow>
                    <div className="mt-3 flex flex-col gap-3">
                      {row.transcript.map((line, i) => (
                        <div key={i} className="flex gap-2.5">
                          <span className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold', line.isAgent ? 'bg-[var(--brand-indigo-tint)] text-[var(--brand-indigo)]' : 'bg-[var(--surface-sunken)] text-[var(--text-secondary)]')}>
                            {line.who.slice(0, 1)}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-baseline gap-2">
                              <span className={cn('text-[12px] font-semibold', line.isAgent ? 'text-[var(--brand-indigo)]' : 'text-[var(--text-primary)]')}>{line.who}</span>
                              <span className="font-[var(--font-mono)] text-[10.5px] text-[var(--text-muted)]">{line.at}</span>
                            </div>
                            <div className="mt-0.5 text-[12.5px] leading-[1.5] text-[var(--text-secondary)]">{line.text}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : row.body || row.subject ? (
                  <div className="mt-5">
                    <Eyebrow>{row.batchCount ? 'The message as it would have gone' : row.channel === 'email' ? 'Email' : 'Message'}</Eyebrow>
                    <div className="mt-2.5 rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-sunken)] px-3.5 py-3">
                      {row.subject ? <div className="mb-1.5 text-[13px] font-bold text-[var(--text-primary)]">{row.subject}</div> : null}
                      {row.body ? <div className="text-[12.5px] leading-[1.55] text-[var(--text-secondary)]">{row.body}</div> : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })()
      ) : null}
    </ViewportDrawer>
  );
}
